import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Phone, Mail, Calendar, FileText } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import QuickContactModal from './QuickContactModal';

export default function StickyCTA() {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showQuickContact, setShowQuickContact] = useState(false);
  const { language } = useLanguage();
  const isTurkish = language === 'tr';

  useEffect(() => {
    const handleScroll = () => {
      // Show after scrolling 300px
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const ctaOptions = [
    {
      icon: Send,
      label: isTurkish ? 'Hızlı Mesaj' : 'Quick Message',
      action: () => {
        setShowQuickContact(true);
        setIsOpen(false);
      },
      color: 'bg-white/5 hover:bg-white/10 border border-white/10'
    },
    {
      icon: Calendar,
      label: isTurkish ? 'Demo Talep Et' : 'Request Demo',
      action: () => {
        document.getElementById('studio')?.scrollIntoView({ behavior: 'smooth' });
        setIsOpen(false);
      },
      color: 'bg-white/5 hover:bg-white/10 border border-white/10'
    },
    {
      icon: Mail,
      label: isTurkish ? 'İletişime Geç' : 'Contact Us',
      action: () => {
        document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
        setIsOpen(false);
      },
      color: 'bg-white/5 hover:bg-white/10 border border-white/10'
    }
  ];

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50" data-testid="sticky-cta">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-16 right-0 mb-2 w-56"
          >
            <div className="bg-[#060710]/95 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <h3 className="text-xs font-mono uppercase tracking-wider text-white/50">
                  {isTurkish ? 'Size Nasıl Yardımcı Olabiliriz' : 'How Can We Help'}
                </h3>
              </div>
              <div className="p-2">
                {ctaOptions.map((option, index) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={index}
                      onClick={option.action}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-white text-sm font-medium transition-colors ${option.color} mb-1 last:mb-0`}
                      data-testid={`sticky-cta-option-${index}`}
                    >
                      <Icon className="w-4 h-4" />
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-12 h-12 shadow-2xl flex items-center justify-center transition-colors border ${
          isOpen 
            ? 'bg-[#060710] border-white/30 hover:border-white/50' 
            : 'bg-white border-white hover:bg-white/90'
        }`}
        data-testid="sticky-cta-toggle"
        aria-label={isTurkish ? 'İletişim menüsü' : 'Contact menu'}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-5 h-5 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageCircle className="w-5 h-5 text-black" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Pulse animation when closed */}
      {!isOpen && (
        <span className="absolute -top-1 -right-1 flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full bg-[#FF3B30] opacity-75"></span>
          <span className="relative inline-flex h-2 w-2 bg-[#FF3B30]"></span>
        </span>
      )}

      {/* Quick Contact Modal */}
      <QuickContactModal 
        isOpen={showQuickContact} 
        onClose={() => setShowQuickContact(false)} 
      />
    </div>
  );
}
